import { MeetingParticipant } from '@/api'
import { Loader, PageLoader } from '@/components'
import { useAuthContext, useSnackbar, useSurveyContext } from '@/providers'
import { paths } from '@/routes/paths'
import { useMeeting } from '@/services'
import {
  postMessageToWebview,
  useInvalidateQuery,
  useModal,
  useNavigateToPreviousLocation,
} from '@/utils'
import { JaaSMeeting } from '@jitsi/react-sdk'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

const useMessages = () => {
  const { t } = useTranslation()

  return {
    snackError: t('global.snackError'),
  }
}

export const VideoCallPage = () => {
  const snackbar = useSnackbar()
  const { currentPatient, user } = useAuthContext()
  const surveyContext = useSurveyContext()
  const navigate = useNavigateToPreviousLocation()
  const readyState = useModal(false)
  const messages = useMessages()
  const invalidateQuery = useInvalidateQuery()

  const { data, isLoading, refetch } = useMeeting(currentPatient?.id!)

  const handleError = () => {
    refetch()
    snackbar.error(messages.snackError)
    navigate(`/${paths.home}/${currentPatient?.id}/dashboard`)
  }

  useEffect(() => {
    if (!isLoading) {
      if (data?.status === 'in_progress') {
        postMessageToWebview({
          type: 'joinMeeting',
          params: {
            meetingId: data.id,
            meetingToken: data.signature,
            meetingRoom: [import.meta.env.VITE_JITSI_APP_ID, data.room_name].join('/'),
            patientId: currentPatient?.id,
            patientName: `${currentPatient?.first_name} ${currentPatient?.last_name}`,
            language: user.language?.toLowerCase(),
          },
          callback: async () => {
            surveyContext.setMeetingSurveyId(data.id)
            readyState.open()
          },
        })
        // @ts-ignore
        if (window.ReactNativeWebView) {
          navigate(`/${paths.home}/${currentPatient?.id}/dashboard`)
        }
      } else {
        handleError()
      }
    }
  }, [isLoading])

  if (isLoading || !readyState.isOpen) return <PageLoader />

  if (data?.status === 'in_progress') {
    return (
      <JaaSMeeting
        appId={import.meta.env.VITE_JITSI_APP_ID!}
        roomName={data.room_name}
        jwt={data.signature}
        configOverwrite={{
          disableThirdPartyRequests: true,
          disableLocalVideoFlip: true,
          backgroundAlpha: 0.5,
          recordingService: {
            enabled: false,
          },
          recordings: {
            showPrejoinWarning: false,
            suggestRecording: false,
          },
        }}
        interfaceConfigOverwrite={{
          VIDEO_LAYOUT_FIT: 'nocrop',
          MOBILE_APP_PROMO: false,
          TILE_VIEW_MAX_COLUMNS: 4,
        }}
        spinner={Loader}
        getIFrameRef={(node) => (node.style.height = '800px')}
        lang={user.language?.toLowerCase()}
        onReadyToClose={() => {
          navigate(`/${paths.home}/${currentPatient?.id}/dashboard`, { meetingSurveyId: data.id })
          invalidateQuery('chatMessages')
        }}
        onApiReady={(externalApi) => {
          externalApi.on('participantJoined', (moderator: MeetingParticipant) => {
            if (
              moderator.displayName ===
                `${currentPatient?.first_name} ${currentPatient?.last_name} (M)` ||
              /\s\(M\)$/.test(moderator.displayName)
            ) {
              externalApi.on('participantLeft', (participant: Pick<MeetingParticipant, 'id'>) => {
                if (participant.id === moderator.id) {
                  externalApi.dispose()
                  invalidateQuery('chatMessages')
                  navigate(`/${paths.home}/${currentPatient?.id}/dashboard`, {
                    meetingSurveyId: data.id,
                  })
                }
              })
            }
          })
        }}
      />
    )
  }

  return <Navigate to={`/${paths.home}/${currentPatient?.id}/dashboard`} />
}
